import { useEffect, useMemo, useState } from "react";
import { FaArrowLeft, FaArrowRight, FaCode, FaColumns, FaCopy, FaDownload, FaEdit, FaExchangeAlt, FaHistory, FaRedo, FaTextWidth } from "react-icons/fa";
import { API_CORE } from "../../../../../_CORE/API/ApiCORE";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { REACT_HOT_TOAST_ERROR, REACT_HOT_TOAST_SUCCESS } from "../../../../../_CORE/HELPERS/reactHotToast.helper";
import { SNIPPETS_CONSTANTS } from "../_MODULES/_API";
import { formatDateTime, formatSize } from "./snippets.helpers";
import { ManualMarkdownEditor } from "../../_Manuals/_UI/ManualMarkdownEditor";

const CURRENT = "CURRENT";

const endpoint = (key, values = {}) => {
    let url = API_CORE.ENDPOINTS[key];
    Object.entries(values).forEach(([name, value]) => { url = url.replace(`:${name}`, String(value)); });
    return url;
};

const unwrap = (response) => response?.data ?? response ?? null;

const versionedFileName = (name = "snippet.txt", versionNumber = "historical") => {
    const dot = name.lastIndexOf(".");
    if (dot <= 0) return `${name}.v${versionNumber}`;
    return `${name.slice(0, dot)}.v${versionNumber}${name.slice(dot)}`;
};

const downloadText = (name, content) => {
    const blob = new Blob([content ?? ""], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

const CodeReadPane = ({ content, wrap = true, label = "Código" }) => {
    const lines = useMemo(() => String(content ?? "").split("\n"), [content]);
    return <div className="LStudio__snippets_reader_code" data-wrap={wrap} aria-label={label}>
        {lines.map((line, index) => <div className="LStudio__snippets_reader_line" key={`${index}-${line.slice(0, 24)}`}>
            <span className="LStudio__snippets_reader_line_number" aria-hidden="true">{index + 1}</span>
            <code>{line || " "}</code>
        </div>)}
    </div>;
};

export const SnippetFileReadModal = ({ file, isOpen, onClose, onEdit, onRestore, onDownload, isLoading = false }) => {
    const [loading, setLoading] = useState(false);
    const [currentContent, setCurrentContent] = useState("");
    const [versions, setVersions] = useState([]);
    const [selectedId, setSelectedId] = useState(CURRENT);
    const [selectedContent, setSelectedContent] = useState("");
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [compareMode, setCompareMode] = useState(false);
    const [wrap, setWrap] = useState(true);

    const currentVersionNumber = useMemo(() => {
        const max = versions.reduce((value, item) => Math.max(value, Number(item.versionNumber || 0)), 0);
        return max + 1;
    }, [versions]);

    const loadCurrent = async () => {
        if (!file?.id || !file?.isText) return { content: "", versions: [] };
        const [contentResponse, versionsResponse] = await Promise.all([
            API_CORE.SERVICES.GET_RESOURCE(endpoint(SNIPPETS_CONSTANTS.FILE_CONTENT, { id: file.id })),
            API_CORE.SERVICES.GET_RESOURCE(endpoint(SNIPPETS_CONSTANTS.VERSION_LIST, { id: file.id })),
        ]);
        const contentData = unwrap(contentResponse);
        const versionData = unwrap(versionsResponse);
        return {
            content: contentData?.content ?? "",
            versions: Array.isArray(versionData) ? versionData : [],
        };
    };

    useEffect(() => {
        if (!isOpen || !file?.id) return;
        setSelectedId(CURRENT);
        setSelectedVersion(null);
        setCompareMode(false);
        if (!file.isText) {
            setCurrentContent("");
            setSelectedContent("");
            setVersions([]);
            return;
        }
        setLoading(true);
        loadCurrent()
            .then((data) => {
                setCurrentContent(data.content);
                setSelectedContent(data.content);
                setVersions(data.versions);
            })
            .catch(() => REACT_HOT_TOAST_ERROR("No se pudo abrir el snippet en modo lectura"))
            .finally(() => setLoading(false));
    }, [isOpen, file?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectVersion = async (nextId) => {
        setSelectedId(nextId);
        setCompareMode(false);
        if (nextId === CURRENT) {
            setSelectedVersion(null);
            setSelectedContent(currentContent);
            return;
        }
        const version = versions.find((item) => item.id === nextId) || null;
        setSelectedVersion(version);
        setSelectedContent("Cargando versión...");
        try {
            const response = await API_CORE.SERVICES.GET_RESOURCE(endpoint(SNIPPETS_CONSTANTS.VERSION_DETAIL, { fileId: file.id, versionId: nextId }));
            const data = unwrap(response);
            setSelectedContent(data?.content ?? "");
        } catch {
            setSelectedContent("");
            REACT_HOT_TOAST_ERROR("No se pudo cargar esa versión");
        }
    };

    const orderedIds = useMemo(() => [CURRENT, ...versions.map((item) => item.id)], [versions]);
    const selectedIndex = Math.max(0, orderedIds.indexOf(selectedId));
    const goPrevious = () => selectedIndex < orderedIds.length - 1 && selectVersion(orderedIds[selectedIndex + 1]);
    const goNext = () => selectedIndex > 0 && selectVersion(orderedIds[selectedIndex - 1]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(selectedContent ?? "");
            REACT_HOT_TOAST_SUCCESS("Código copiado");
        } catch {
            REACT_HOT_TOAST_ERROR("No se pudo copiar el código");
        }
    };

    const downloadSelected = () => {
        if (selectedId === CURRENT) return onDownload?.(file);
        if (!selectedVersion) return;
        downloadText(versionedFileName(file.name, selectedVersion.versionNumber), selectedContent);
    };

    const restoreSelected = async () => {
        if (!selectedVersion) return;
        const ok = await confirm.warning(
            `Restaurar versión ${selectedVersion.versionNumber}`,
            "NOTEBOOK guardará el estado actual como un nuevo snapshot antes de restaurar. No perdés la versión que tenés ahora.",
            { confirmText: "Restaurar", cancelText: "Cancelar" },
        );
        if (!ok) return;
        const result = await onRestore?.(file, selectedVersion);
        if (result?.success === false || result === false) return;
        setLoading(true);
        try {
            const data = await loadCurrent();
            setCurrentContent(data.content);
            setSelectedContent(data.content);
            setVersions(data.versions);
            setSelectedId(CURRENT);
            setSelectedVersion(null);
            setCompareMode(false);
        } finally {
            setLoading(false);
        }
    };

    if (!file) return null;

    const versionLabel = selectedId === CURRENT
        ? `Actual · v${currentVersionNumber}`
        : `Histórico · v${selectedVersion?.versionNumber ?? "-"}`;

    const isMarkdown = file.isText && (
        String(file.language || "").toLowerCase() === "markdown"
        || /\.(md|markdown)$/i.test(file.name || "")
    );

    return <AppModal
        isOpen={isOpen}
        onClose={onClose}
        size="full"
        className="LStudio__snippets_reader_modal"
        title={file.name}
        description={`${file.path || file.name} · ${file.language || "archivo"} · ${formatSize(file.size)}`}
        footer={<>
            <AppButton variant="outline" tone="neutral" onClick={onClose}>Cerrar</AppButton>
            {file.isText && selectedVersion && <AppButton variant="outline" tone="neutral" icon={<FaRedo />} onClick={restoreSelected} disabled={loading || isLoading}>Restaurar versión</AppButton>}
            <AppButton icon={<FaEdit />} onClick={() => onEdit?.(file)}>Editar</AppButton>
        </>}
    >
        {!file.isText ? <div className="LStudio__snippets_binary LStudio__snippets_reader_binary">
            <FaCode />
            <strong>Archivo binario</strong>
            <span>Podés conservarlo y descargarlo, pero el modo lectura solo interpreta archivos de texto.</span>
            <AppButton icon={<FaDownload />} onClick={() => onDownload?.(file)}>Descargar archivo</AppButton>
        </div> : <div className="LStudio__snippets_reader">
            <div className="LStudio__snippets_reader_toolbar">
                <div className="LStudio__snippets_reader_version">
                    <FieldSelect id="snippet_reader_version" label="Versión" value={selectedId} onChange={(event) => selectVersion(event.target.value)} isDisabled={loading} options={[{ value: CURRENT, label: `Actual · v${currentVersionNumber} · ${formatDateTime(file.updatedAt)}` }, ...versions.map((version) => ({ value: version.id, label: `v${version.versionNumber} · ${formatDateTime(version.createdAt)} · ${version.note || "Snapshot automático"}` }))]} isSearchable={versions.length >= 8} />
                </div>
                <div className="LStudio__snippets_reader_nav" aria-label="Navegar versiones">
                    <AppButton size="sm" variant="outline" tone="neutral" icon={<FaArrowLeft />} onClick={goPrevious} disabled={selectedIndex >= orderedIds.length - 1 || loading}>Más antigua</AppButton>
                    <AppButton size="sm" variant="outline" tone="neutral" icon={<FaArrowRight />} onClick={goNext} disabled={selectedIndex <= 0 || loading}>Más nueva</AppButton>
                </div>
                <div className="LStudio__snippets_reader_actions">
                    {!isMarkdown && <AppButton size="sm" variant="outline" tone="neutral" icon={<FaTextWidth />} onClick={() => setWrap((value) => !value)}>{wrap ? "No ajustar" : "Ajustar líneas"}</AppButton>}
                    {selectedVersion && <AppButton size="sm" variant="outline" tone="neutral" icon={<FaExchangeAlt />} onClick={() => setCompareMode((value) => !value)}>{compareMode ? "Solo lectura" : "Comparar con actual"}</AppButton>}
                    <AppButton size="sm" variant="outline" tone="neutral" icon={<FaCopy />} onClick={copy}>Copiar</AppButton>
                    <AppButton size="sm" variant="outline" tone="neutral" icon={<FaDownload />} onClick={downloadSelected}>Descargar</AppButton>
                </div>
            </div>

            <div className="LStudio__snippets_reader_meta">
                <span><FaHistory /> {versionLabel}</span>
                {selectedVersion ? <><span>{formatDateTime(selectedVersion.createdAt)}</span><span>{selectedVersion.note || "Snapshot automático"}</span></> : <><span>{formatDateTime(file.updatedAt)}</span><span>Estado actual del archivo</span></>}
            </div>

            {loading ? <div className="LStudio__snippets_demo_loading">Cargando contenido...</div> : compareMode && selectedVersion ? <div className="LStudio__snippets_reader_compare">
                <section>
                    <header><FaColumns /><div><strong>Versión {selectedVersion.versionNumber}</strong><span>{formatDateTime(selectedVersion.createdAt)}</span></div></header>
                    <CodeReadPane content={selectedContent} wrap={wrap} label={`Versión ${selectedVersion.versionNumber}`} />
                </section>
                <section>
                    <header><FaCode /><div><strong>Actual · v{currentVersionNumber}</strong><span>{formatDateTime(file.updatedAt)}</span></div></header>
                    <CodeReadPane content={currentContent} wrap={wrap} label="Versión actual" />
                </section>
            </div> : isMarkdown ? <ManualMarkdownEditor value={selectedContent} readOnly minHeight={560} ariaLabel={versionLabel} /> : <CodeReadPane content={selectedContent} wrap={wrap} label={versionLabel} />}
        </div>}
    </AppModal>;
};
